import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { AvailabilitySlot } from './OpenTableAvailability';
import { uniqueBookableSlots } from '../utils/availability';

// Normalize both legacy 12-hour times and the API's 24-hour times before
// grouping, sorting or deduplicating a sitting.
function normalizeTime(value: string): string | null {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::00)?(?:\s*(AM|PM))?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3]?.toUpperCase();
  if (minute > 59 || (period ? hour < 1 || hour > 12 : hour > 23)) return null;
  if (period) hour = hour % 12 + (period === 'PM' ? 12 : 0);
  return `${String(hour).padStart(2, '0')}:${match[2]}`;
}

const dateLabel = (date: string) => new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
  weekday: 'short', day: 'numeric', month: 'short',
});

const AvailabilityTable: React.FC<{ slots: AvailabilitySlot[] }> = ({ slots }) => {
  const [selected, setSelected] = useState<{ date: string; time: string } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const normalized = uniqueBookableSlots(slots.flatMap(slot => {
    const time = normalizeTime(slot.available_time);
    return time ? [{ ...slot, available_time: time }] : [];
  }));
  const dates = Array.from(new Set(normalized.map(slot => slot.date))).sort();
  const times = Array.from(new Set(normalized.map(slot => slot.available_time))).sort();
  const cells = new Map<string, AvailabilitySlot[]>();
  normalized.forEach(slot => {
    const key = `${slot.date}/${slot.available_time}`;
    cells.set(key, [...(cells.get(key) || []), slot]);
  });
  const selectedSlots = selected ? cells.get(`${selected.date}/${selected.time}`) || [] : [];
  const restaurants = new Map<number, AvailabilitySlot[]>();
  selectedSlots.forEach(slot => restaurants.set(slot.venue_id, [...(restaurants.get(slot.venue_id) || []), slot]));

  useEffect(() => { if (selected) heading.current?.focus(); }, [selected]);
  const close = () => { setSelected(null); trigger.current?.focus(); };

  return <section aria-label="Availability timetable" className="availability-timetable space-y-4">
    <p id="availability-table-help" className="text-sm text-ink-600">
      Tap a count to see which restaurants are available.
    </p>
    <div className="overflow-auto max-h-[65vh] rounded-xl border border-ink-200 bg-surface" tabIndex={0} role="region" aria-label="Scrollable availability table">
      <table style={{ '--mobile-table-width': `${44 + dates.length * 40}px` } as React.CSSProperties} className="w-full border-separate border-spacing-0 text-sm text-center" aria-describedby="availability-table-help">
        <caption className="sr-only">Available restaurants by date and time</caption>
        <thead className="sticky top-0 z-20">
          <tr>
            <th scope="col" className="sticky left-0 z-30 bg-ink-50 border-b border-ink-200 p-4 text-left">Time</th>
            {dates.map(date => <th key={date} scope="col" className="min-w-[8rem] bg-ink-50 border-b border-ink-200 p-4 font-medium">
              <time dateTime={date} aria-label={dateLabel(date)}>
                <span className="hidden sm:inline">{dateLabel(date)}</span>
                <span className="sm:hidden" aria-hidden="true"><span className="block text-[10px]">{new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' })}</span><span className="block text-sm">{new Date(`${date}T12:00:00`).getDate()}</span></span>
              </time>
            </th>)}
          </tr>
        </thead>
        <tbody>
          {times.map(time => <tr key={time}>
            <th scope="row" className="sticky left-0 z-10 bg-surface border-b border-ink-100 p-4 text-left font-medium tabular-nums">{time}</th>
            {dates.map(date => {
              const cellSlots = cells.get(`${date}/${time}`) || [];
              const count = new Set(cellSlots.map(slot => slot.venue_id)).size;
              const active = selected?.date === date && selected.time === time;
              return <td key={date} className="border-b border-ink-100 p-1.5">
                {count ? <button type="button" data-pro-tour="booking"
                  aria-label={`${count} ${count === 1 ? 'restaurant' : 'restaurants'} on ${dateLabel(date)} at ${time}`}
                  aria-expanded={active} aria-controls={active ? 'availability-slot-details' : undefined}
                  onClick={event => { trigger.current = event.currentTarget; setSelected({ date, time }); }}
                  className={`w-full min-h-[44px] rounded-lg font-semibold tabular-nums transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600 ${active ? 'bg-brand-600 text-white' : 'bg-brand-50 text-brand-800 hover:bg-brand-100'}`}>
                  {count}
                </button> : <span className="text-ink-400" aria-label="No restaurants">0</span>}
              </td>;
            })}
          </tr>)}
        </tbody>
      </table>
    </div>
    {selected && <section id="availability-slot-details" aria-labelledby="availability-slot-heading"
      onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
      className="rounded-xl border border-brand-200 bg-surface p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 id="availability-slot-heading" ref={heading} tabIndex={-1} className="font-display text-2xl">
            {dateLabel(selected.date)} · {selected.time}
          </h2>
          <p className="text-sm text-ink-600 mt-1">{restaurants.size} {restaurants.size === 1 ? 'restaurant' : 'restaurants'} · {selectedSlots.length} booking {selectedSlots.length === 1 ? 'option' : 'options'}</p>
        </div>
        <button type="button" aria-label="Close restaurant list" onClick={close} className="p-3 rounded-lg hover:bg-ink-50"><X size={20} /></button>
      </div>
      <ul className="divide-y divide-ink-100">
        {Array.from(restaurants.entries()).sort(([, a], [, b]) => a[0].restaurant.localeCompare(b[0].restaurant)).map(([venueId, options]) => <li key={venueId} className="availability-restaurant-row py-4">
          <h3 className="min-w-0 font-display text-xl">{options[0].restaurant}</h3>
          <div className="availability-booking-options">
            {options.sort((a, b) => a.party_size - b.party_size).map(slot => <a key={slot.party_size} href={slot.booking_url} target="_blank" rel="noopener noreferrer"
              aria-label={`Book ${slot.restaurant} for ${slot.party_size} at ${slot.available_time}`}
              className="inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-lg border border-brand-200 px-4 py-3 text-sm text-brand-800 hover:bg-brand-50">
              Table for {slot.party_size}<span className="ml-2" aria-hidden="true">↗</span>
            </a>)}
          </div>
        </li>)}
      </ul>
      <p className="text-xs text-ink-500 mt-4">Availability is confirmed on the restaurant’s booking page.</p>
    </section>}
  </section>;
};

export default AvailabilityTable;
